import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRightLeft,
    Bell,
    Calendar,
    Check,
    ClipboardList,
    FileText,
    MessageSquare,
    Phone,
    X,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
} from "@/services/notification.service";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useToast } from "@/components/shared/Toast";
import type { Notification, NotificationType } from "@/types/notification";
import { HigIconBadge, higToolbarButtonClass } from "@/components/ui/hig-icon";
import { cn } from "@/lib/utils";

const TYPE_ICON: Record<NotificationType, React.ComponentType<{ className?: string }>> = {
    appointment: Calendar,
    ticket: ClipboardList,
    message: MessageSquare,
    invoice: FileText,
    ai_phone: Phone,
    referral: ArrowRightLeft,
    system: Bell,
};

export function NotificationPanel() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const bellRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const loadNotifications = useCallback(async () => {
        if (!user?.id) return;
        const [items, count] = await Promise.all([
            getNotifications(user.id, { limit: 20 }),
            getUnreadCount(user.id),
        ]);
        setNotifications(items);
        setUnreadCount(count);
    }, [user?.id]);

    // Poll unread count every 60s
    useEffect(() => {
        if (!user?.id) return;

        async function pollCount() {
            const count = await getUnreadCount(user!.id!);
            setUnreadCount(count);
        }

        pollCount();
        const interval = setInterval(pollCount, 60_000);
        return () => clearInterval(interval);
    }, [user?.id]);

    // Realtime: instant badge update + toast on new notification inserts
    useEffect(() => {
        if (!user?.id || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`notifications:${user.id}`)
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "notifications",
                    filter: `user_id=eq.${user.id}`,
                },
                (payload) => {
                    setUnreadCount((c) => c + 1);

                    const row = payload.new as Record<string, unknown>;
                    const title = typeof row.title === "string" ? row.title : "Neue Benachrichtigung";
                    const body = typeof row.body === "string" ? row.body : "";
                    const notifType = typeof row.notification_type === "string" ? row.notification_type : "";

                    const isReferral = notifType.startsWith("referral") || notifType === "new_referral";
                    addToast({
                        type: isReferral ? "info" : "success",
                        title,
                        description: body.length > 80 ? body.slice(0, 77) + "…" : body,
                    });

                    if (open) loadNotifications();
                },
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user?.id, open, loadNotifications, addToast]);

    // Load full list when panel opens
    useEffect(() => {
        if (open) loadNotifications();
    }, [open, loadNotifications]);

    // ESC to close + return focus to bell
    useEffect(() => {
        if (!open) return;
        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") {
                setOpen(false);
                bellRef.current?.focus();
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open]);

    async function handleMarkAllRead() {
        if (!user?.id) return;
        await markAllAsRead(user.id);
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
    }

    async function handleClick(notification: Notification) {
        if (!notification.isRead) {
            await markAsRead(notification.id);
            setNotifications((prev) =>
                prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)),
            );
            setUnreadCount((c) => Math.max(0, c - 1));
        }
        if (notification.actionUrl) {
            navigate(notification.actionUrl);
            setOpen(false);
        }
    }

    function formatTimeAgo(dateStr: string): string {
        const now = new Date();
        const date = new Date(dateStr);
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60_000);

        if (diffMin < 1) return "Gerade eben";
        if (diffMin < 60) return `Vor ${diffMin} Min.`;
        const diffHours = Math.floor(diffMin / 60);
        if (diffHours < 24) return `Vor ${diffHours} Std.`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays === 1) return "Gestern";
        if (diffDays < 7) return `Vor ${diffDays} Tagen`;
        return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    }

    const panelShadow =
        "0 0 0 1px rgba(26,24,22,0.04), 0 1px 2px rgba(26,24,22,0.02), 0 10px 26px -14px rgba(26,24,22,0.18), 0 22px 44px -26px rgba(26,24,22,0.12)";

    return (
        <div className="relative">
            {/* Bell Button */}
            <button
                ref={bellRef}
                onClick={() => setOpen(!open)}
                className={cn("relative", higToolbarButtonClass)}
                aria-label={`Benachrichtigungen${unreadCount > 0 ? ` (${unreadCount} ungelesen)` : ""}`}
            >
                <Bell className="h-[18px] w-[18px]" />
                <AnimatePresence>
                    {unreadCount > 0 && (
                        <motion.span
                            className="absolute -right-1 -top-1 flex h-[19px] min-w-[19px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-[0_8px_18px_-10px_rgba(239,68,68,0.9)] ring-2 ring-white dark:ring-neutral-950"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 500, damping: 25 }}
                        >
                            {unreadCount > 99 ? "99+" : unreadCount}
                        </motion.span>
                    )}
                </AnimatePresence>
            </button>

            {/* Dropdown Panel */}
            <AnimatePresence>
                {open && (
                    <>
                        <div
                            className="fixed inset-0 z-30"
                            onClick={() => {
                                setOpen(false);
                                bellRef.current?.focus();
                            }}
                        />
                        <motion.div
                            ref={panelRef}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Benachrichtigungen"
                            className="absolute right-0 top-full mt-2 z-40 w-96 overflow-hidden rounded-2xl border"
                            style={{
                                backgroundColor: "var(--surface-primary)",
                                borderColor: "var(--border-subtle)",
                                boxShadow: panelShadow,
                            }}
                            initial={{ opacity: 0, scale: 0.95, y: -4 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -4 }}
                            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between border-b px-5 py-3.5" style={{ borderColor: "var(--border-subtle)" }}>
                                <h3 className="text-sm font-bold text-neutral-900 tracking-tight">
                                    Benachrichtigungen
                                </h3>
                                <div className="flex items-center gap-1">
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={handleMarkAllRead}
                                            className="flex items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-xs font-medium text-neutral-600 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
                                            aria-label="Alle als gelesen markieren"
                                        >
                                            <Check className="h-3.5 w-3.5" />
                                            Alle gelesen
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="rounded-[10px] p-1.5 text-neutral-400 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                                        aria-label="Schließen"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {/* List */}
                            <div className="max-h-[420px] overflow-y-auto">
                                {notifications.length === 0 ? (
                                    <div className="flex flex-col items-center py-12 text-center">
                                        <Bell className="mb-3 h-8 w-8 text-neutral-200 dark:text-neutral-700" />
                                        <p className="text-sm text-neutral-400">
                                            Keine Benachrichtigungen
                                        </p>
                                    </div>
                                ) : (
                                    notifications.map((notification) => {
                                        const Icon = TYPE_ICON[notification.type] || Bell;

                                        return (
                                            <button
                                                key={notification.id}
                                                onClick={() => handleClick(notification)}
                                                className={`flex w-full items-start gap-3 px-5 py-3.5 text-left transition-all duration-200 hover:bg-neutral-50/80 dark:hover:bg-neutral-900/60 ${
                                                    !notification.isRead ? "bg-brand-50/20" : ""
                                                }`}
                                            >
                                                <HigIconBadge className="mt-0.5" size="sm">
                                                    <Icon className="h-4 w-4" />
                                                </HigIconBadge>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p
                                                            className={`text-sm ${
                                                                !notification.isRead
                                                                    ? "font-semibold text-neutral-800"
                                                                    : "text-neutral-700"
                                                            }`}
                                                        >
                                                            {notification.title}
                                                        </p>
                                                        {!notification.isRead && (
                                                            <span
                                                                className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500"
                                                                aria-hidden="true"
                                                            />
                                                        )}
                                                    </div>
                                                    <p className="mt-0.5 text-xs text-neutral-500 line-clamp-2">
                                                        {notification.body}
                                                    </p>
                                                    <p className="mt-1 text-2xs text-neutral-500">
                                                        {formatTimeAgo(notification.createdAt)}
                                                    </p>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
